import { motion, useReducedMotion } from "framer-motion";
import { Scan, FolderLock, ListChecks, PenNib, SealCheck, Sparkle, ShareNetwork } from "@phosphor-icons/react";

// Truthful capability journey. Every stage maps to a LIVE DuruVault capability;
// DuruVerify runs in early-access (sandbox) and is labelled as such. No coming-soon here.
const STAGES = [
  { key: "capture", stage: "CAPTURE", name: "DuruScan", note: "Scan & digitize", icon: Scan },
  { key: "store", stage: "STORE", name: "Secure vault", note: "Encrypted storage", icon: FolderLock },
  { key: "organize", stage: "ORGANIZE", name: "OCR & tags", note: "Auto-classify", icon: ListChecks },
  { key: "sign", stage: "SIGN", name: "DuruSign", note: "e-Signatures", icon: PenNib },
  { key: "verify", stage: "VERIFY", name: "DuruVerify", note: "Early access", icon: SealCheck },
  { key: "automate", stage: "AUTOMATE", name: "DuruTwin", note: "Living metadata", icon: Sparkle },
  { key: "share", stage: "SHARE", name: "Secure links", note: "Password & expiry", icon: ShareNetwork },
];

export default function DocumentFlow() {
  const reduce = useReducedMotion();
  return (
    <section className="relative border-y border-white/10 bg-white/[0.02]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="subhead text-xs" data-testid="section-overline">One document journey</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">One document. Every step. One secure vault.</h2>
          <p className="mt-3 text-white/60 text-sm sm:text-base">Every DuruVault capability works on the same document, end to end.</p>
        </div>

        <div className="relative mt-14" data-testid="document-flow">
          {/* Continuous connecting rail — horizontal (lg) */}
          <div className="hidden lg:block absolute left-7 right-7 top-7 h-[2px] rounded-full bg-gradient-to-r from-primary/20 via-primary/45 to-primary/20 overflow-hidden" aria-hidden="true">
            {!reduce && (
              <motion.span
                className="absolute top-0 h-[2px] w-24 bg-gradient-to-r from-transparent via-primary to-transparent"
                initial={{ x: "-96px" }}
                animate={{ x: ["-96px", "100%"] }}
                transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}
              />
            )}
          </div>
          {/* Continuous connecting rail — vertical (mobile) */}
          <div className="lg:hidden absolute left-[27px] top-7 bottom-7 w-[2px] rounded-full bg-gradient-to-b from-primary/20 via-primary/45 to-primary/20 overflow-hidden" aria-hidden="true">
            {!reduce && (
              <motion.span
                className="absolute left-0 w-[2px] h-16 bg-gradient-to-b from-transparent via-primary to-transparent"
                initial={{ y: "-64px" }}
                animate={{ y: ["-64px", "100%"] }}
                transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}
              />
            )}
          </div>

          <div className="relative flex flex-col gap-7 lg:flex-row lg:gap-3">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.key}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                data-testid={`flow-node-${s.key}`}
                className="group relative flex items-center gap-4 lg:flex-1 lg:flex-col lg:items-center lg:gap-2.5 lg:text-center"
              >
                <span className="relative z-10 grid place-items-center w-14 h-14 rounded-2xl bg-[#41464b] border border-primary/25 text-primary shrink-0 shadow-[0_0_18px_rgba(212,175,55,0.10)] transition-transform duration-200 group-hover:scale-105 group-hover:border-primary/60">
                  <s.icon size={26} weight="duotone" />
                  <span className="absolute -top-2 -right-2 grid place-items-center w-5 h-5 rounded-full bg-primary text-[#2a2d31] text-[9px] font-mono font-bold shadow">{String(i + 1).padStart(2, "0")}</span>
                </span>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.25em] text-primary">{s.stage}</div>
                  <div className="text-sm font-semibold text-white leading-tight">{s.name}</div>
                  <div className="text-xs text-white/45 mt-0.5">{s.note}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
